import { escapeHtml, formatCount, formatDuration } from '../format';
import { CONVERSATION_GAP_MINUTES, MIN_REPLIES_FOR_SPEED } from '../stats/constants';
import type { ChatReport, ChatStats, Grade, ReportSubject, SubjectId } from '../types';
import { NAME_IMAGE_WARNING, warningMessage } from './copy';
import { formatAverage, formatPeriod, roomTitle } from './format-ui';
import { icon } from './icons';

/** Result sections above and below the statistic cards (spec §6.2-4). */

const SUBJECT_ICONS: Readonly<Record<SubjectId, string>> = {
  initiative: '🙋',
  'reply-speed': '⚡',
  'night-owl': '🌙',
  laughter: '😆',
  expression: '🎨',
  'long-form': '✍️',
};

/**
 * Escapes rule-based copy and keeps short ‘quoted words’ on one line
 * (WebKit may break right after ‘ under word-break: keep-all).
 */
export function copyMarkup(value: string): string {
  return escapeHtml(value).replace(/‘([^‘’]{1,12})’/g, '<span class="krc-nobr">‘$1’</span>');
}

/** Subject cards list at most this many people before folding the rest. */
export const MAX_GRADES_SHOWN = 6;
export const MAX_LINES_SHOWN = 5;

export function coverMarkup(report: ChatReport, isSample: boolean): string {
  const { stats, card } = report;
  const me = stats.people.find((person) => person.isMe);
  return `
    <header class="krc-cover">
      <div class="krc-cover-top">
        <span class="krc-cover-kicker">${isSample ? '샘플 성적표' : '우리 사이 성적표'}</span>
        <span class="krc-cover-period">${escapeHtml(formatPeriod(stats.totals.firstAt, stats.totals.lastAt))}</span>
      </div>
      <h2 class="krc-cover-title" id="krc-result-title" tabindex="-1">${escapeHtml(roomTitle(stats))}</h2>
      ${stats.roomKind === 'group' ? `<p class="krc-cover-people">${formatCount(stats.people.length)}명의 단톡방${me ? ` · 나: ${escapeHtml(me.name)}` : ''}</p>` : me ? `<p class="krc-cover-people">나: ${escapeHtml(me.name)}</p>` : ''}
      <dl class="krc-cover-stats">
        <div><dt>총 메시지</dt><dd>${formatCount(stats.totals.messages)}<small>개</small></dd></div>
        <div><dt>하루 평균</dt><dd>${formatAverage(stats.totals.perDay)}<small>개</small></dd></div>
        <div><dt>함께한 기간</dt><dd>${formatCount(stats.totals.spanDays)}<small>일</small></dd></div>
      </dl>
      <figure class="krc-headline">
        <figcaption>담임 총평</figcaption>
        <blockquote><p>${copyMarkup(card.headline)}</p></blockquote>
      </figure>
    </header>`;
}

export function sampleBannerMarkup(): string {
  return `<p class="krc-sample-banner" role="note">${icon('sparkle')}<span>가상의 샘플 대화로 만든 성적표예요. 내 대화로 해 보려면 아래의 ‘다른 대화 분석하기’를 눌러 주세요.</span></p>`;
}

function gradeChip(grade: Grade): string {
  return `<span class="krc-grade" data-grade="${grade}"><span class="krc-sr-only">등급 </span>${grade}</span>`;
}

function subjectMarkup(subject: ReportSubject, stats: ChatStats): string {
  const byIndex = new Map(stats.people.map((person) => [person.index, person]));
  const scoreItem = (score: ReportSubject['scores'][number]) => {
    const person = byIndex.get(score.person);
    return `<li>${gradeChip(score.grade)}<span class="krc-grade-name">${escapeHtml(person?.name ?? '')}${person?.isMe ? ' <span class="krc-me-badge">나</span>' : ''}</span><span class="krc-grade-value">${escapeHtml(score.valueLabel)}</span></li>`;
  };
  const shown = subject.scores.slice(0, MAX_GRADES_SHOWN);
  const rest = subject.scores.slice(MAX_GRADES_SHOWN);
  const grades = subject.scores.length
    ? `<ul class="krc-grades">${shown.map(scoreItem).join('')}</ul>${rest.length ? `<details class="krc-more-grades"><summary>나머지 ${formatCount(rest.length)}명 보기</summary><ul class="krc-grades">${rest.map(scoreItem).join('')}</ul></details>` : ''}`
    : '<p class="krc-grade-pending"><span class="krc-grade" data-grade="none">채점 보류</span></p>';
  return `
    <article class="krc-subject" aria-labelledby="krc-subject-${subject.id}">
      <h3 id="krc-subject-${subject.id}"><span class="krc-subject-icon" aria-hidden="true">${SUBJECT_ICONS[subject.id] ?? '📘'}</span>${escapeHtml(subject.title)}</h3>
      ${grades}
      <p class="krc-subject-comment"><span class="krc-sr-only">담임 한마디: </span>${copyMarkup(subject.comment)}</p>
    </article>`;
}

export function subjectsMarkup(report: ChatReport): string {
  return `
    <section class="krc-section" aria-labelledby="krc-subjects-title">
      <div class="krc-section-head"><h2 id="krc-subjects-title">과목별 성적</h2><p>A+부터 D까지, ‘얼마나 그런 편인지’를 매겼어요.</p></div>
      <div class="krc-subjects">${report.card.subjects.map((subject) => subjectMarkup(subject, report.stats)).join('')}</div>
      <p class="krc-footnote">등급은 습관이 얼마나 두드러지는지 보여 줄 뿐, 잘하고 못하고가 아니에요.</p>
    </section>`;
}

export function awardsMarkup(report: ChatReport): string {
  const byIndex = new Map(report.stats.people.map((person) => [person.index, person]));
  const awards = report.card.awards.filter((award) => byIndex.has(award.person));
  const body = awards.length
    ? `<ul class="krc-awards">${awards.map((award) => `
        <li class="krc-award">
          <span class="krc-award-medal" aria-hidden="true">상</span>
          <div>
            <p class="krc-award-title">${escapeHtml(award.title)}</p>
            <p class="krc-award-name">${escapeHtml(byIndex.get(award.person)!.name)}</p>
            <p class="krc-award-reason">${copyMarkup(award.reason)}</p>
          </div>
        </li>`).join('')}</ul>`
    : '<p class="krc-empty">이번엔 상을 줄 만큼 뚜렷한 기록이 없었어요. 다음 학기를 기대해요.</p>';
  return `
    <section class="krc-section" aria-labelledby="krc-awards-title">
      <div class="krc-section-head"><h2 id="krc-awards-title">상장</h2><p>기록으로 뽑은 이 방의 수상자예요.</p></div>
      ${body}
    </section>`;
}

export function linesMarkup(report: ChatReport): string {
  const lines = report.card.lines.slice(0, MAX_LINES_SHOWN);
  const body = lines.length
    ? `<ol class="krc-lines">${lines.map((line) => `<li>${copyMarkup(line.text)}</li>`).join('')}</ol>`
    : '<p class="krc-empty">담임 선생님이 아직 고민 중이에요. 대화가 더 쌓이면 한마디가 늘어나요.</p>';
  return `
    <section class="krc-section krc-card krc-lines-card" aria-labelledby="krc-lines-title">
      <div class="krc-section-head"><h2 id="krc-lines-title">담임 한마디 TOP ${MAX_LINES_SHOWN}</h2><p>숫자로 본 우리 사이, 다정한 팩폭으로.</p></div>
      ${body}
    </section>`;
}

export function actionsMarkup(): string {
  return `
    <section class="krc-card krc-actions" aria-labelledby="krc-actions-title">
      <h2 id="krc-actions-title">성적표 남기기</h2>
      <p class="krc-name-warning">${icon('alert')}<span>${escapeHtml(NAME_IMAGE_WARNING)}</span></p>
      <div class="krc-action-buttons">
        <button class="button button-primary" type="button" data-action="save" disabled>${icon('download')}<span data-label>이미지 준비 중…</span></button>
        <button class="button krc-kakao-button" type="button" data-action="kakao">${icon('chat')}<span data-label>카카오톡으로 알려주기</span></button>
      </div>
      <p class="krc-action-note">카카오톡으로는 결과가 아니라 카톡 성적표 링크만 보내요.</p>
      <div class="krc-action-links">
        <button class="krc-text-button" type="button" data-action="copy-link">${icon('link')}<span>링크 복사</span></button>
        <button class="krc-text-button" type="button" data-action="edit">${icon('edit')}<span>이름 다시 정리하기</span></button>
      </div>
      <button class="button button-soft" type="button" data-action="restart">${icon('refresh')}<span>다른 대화 분석하기</span></button>
    </section>`;
}

export function notesMarkup(stats: ChatStats): string {
  const notes = [
    '카톡은 분 단위로 기록돼서 답장 시간은 대략이에요.',
    'PC에서 내보낸 파일에는 채팅방에 불러온 메시지만 들어 있어요.',
    `${formatDuration(CONVERSATION_GAP_MINUTES)} 이상 조용하면 새 대화로 셌어요.`,
    `답장이 ${MIN_REPLIES_FOR_SPEED}개보다 적은 사람은 답장 속도를 재지 않았어요.`,
    '입장·퇴장 같은 안내 메시지는 세지 않았어요.',
    '통계와 문구는 재미를 위한 관찰이에요. 관계를 판정하지 않아요.',
  ];
  const warnings = stats.warnings
    .filter((warning) => warning.code !== 'EXPORTER_LABEL_DETECTED')
    .map((warning) => `<li class="is-warning">${escapeHtml(warningMessage(warning))}</li>`);
  return `
    <section class="krc-section krc-notes" aria-labelledby="krc-notes-title">
      <h2 id="krc-notes-title">정확도 안내</h2>
      <ul>${warnings.join('')}${notes.map((note) => `<li>${escapeHtml(note)}</li>`).join('')}</ul>
    </section>`;
}
